import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { ELEMENT_KIND_LABELS } from "./LocationClocksCard";
import { useParcelTrack, useParcelTrackMutation } from "./hooks";
import type { ParcelTrackElement } from "./types";

/**
 * Tor lokalizacji działki: zegar (kategoria · aktywny bloczek · my/oni · dni/limit)
 * + edytowalna oś bloczków (auto z artefaktów + ręczne) + „Dodaj element” na żywo,
 * gdy sytuacja ewoluuje (uzupełnienie, zgłoszenie do ZE, ponowny teren, art. 124…).
 */

const STATUS_LABELS: Record<string, string> = {
  planned: "zaplanowany", active: "aktywny", done: "zakończony",
  failed: "nieudany (odmowa)", cancelled: "anulowany",
};
const STATUS_COLORS = {
  planned: "gray", active: "blue", done: "green", failed: "red", cancelled: "gray",
} as const;

function ElementRow({ el, onPatch, onDelete }: {
  el: ParcelTrackElement;
  onPatch: (body: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  const open = el.status === "planned" || el.status === "active";
  return (
    <div className="flex items-start gap-2 border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-elevated)]">
      <span className="mt-0.5 shrink-0 text-sm">
        {el.status === "done" ? "✓" : el.status === "failed" ? "✗" : el.status === "active" ? "▶" : "◌"}
      </span>
      <div className="flex-1 min-w-0 text-xs">
        <div className="flex flex-wrap items-center gap-1.5">
          <b>{el.kind_label}</b>
          <Badge color={STATUS_COLORS[el.status] ?? "gray"}>{STATUS_LABELS[el.status] ?? el.status}</Badge>
          {el.is_auto && <span className="text-[9px] font-mono text-[var(--color-text-muted)]" title="Zasiany przez system (szablon/artefakt)">auto</span>}
          {el.kind === "inny" && (el.manual_our_days || el.manual_their_days) && (
            <span className="font-mono text-[10px]">({el.manual_our_days ?? 0}d / {el.manual_their_days ?? 0}d)</span>
          )}
        </div>
        {el.proceeding_note && <div className="text-[var(--color-text-muted)] truncate">↪ {el.proceeding_note}</div>}
        {el.outcome_note && <div className="text-[var(--color-text-muted)]">{el.outcome_note}</div>}
      </div>
      {open && (
        <div className="flex flex-col items-end gap-0.5 text-[10px] shrink-0">
          {el.status === "planned" && (
            <button className="text-[var(--color-primary)]" onClick={() => onPatch({ status: "active" })}>start</button>
          )}
          {el.status === "active" && !el.started_at && (
            <button className="text-[var(--color-primary)]" title="Nasza faza zakończona — zegar przechodzi na drugą stronę"
              onClick={() => onPatch({ mark_sent: true })}>wysłane →</button>
          )}
          <button className="text-green-700" onClick={() => onPatch({ status: "done" })}>zakończ ✓</button>
          <button className="text-red-700" onClick={() => onPatch({ status: "failed" })}>odmowa ✗</button>
          {el.status === "planned"
            ? <button className="text-[var(--color-text-muted)]" onClick={onDelete}>usuń</button>
            : <button className="text-[var(--color-text-muted)]" onClick={() => onPatch({ status: "cancelled" })}>anuluj</button>}
        </div>
      )}
    </div>
  );
}

export function ParcelTrackModal({ parcelId, ident, onClose }: {
  parcelId: string;
  ident?: string;
  onClose: () => void;
}) {
  // Modal żyje wyłącznie w przestrzeni /power-projects/:id (zakładka Doradca),
  // gdzie :id to id PRZEDSIĘWZIĘCIA — link do kreatora wniosków musi go użyć,
  // a nie connection_id (route rozwiązuje przyłącze przez by-project/{project_id}).
  const { id: projectId } = useParams<{ id: string }>();
  const { data: track, isLoading } = useParcelTrack(parcelId);
  const m = useParcelTrackMutation(parcelId);
  const [addOpen, setAddOpen] = useState(false);
  const [kind, setKind] = useState("wniosek");
  const [label, setLabel] = useState("");
  const [ourDays, setOurDays] = useState<string>("");
  const [theirDays, setTheirDays] = useState<string>("");

  const clock = track?.clock;
  return (
    <Modal open onClose={onClose} title={`Tor uzgodnienia — działka ${ident ?? ""}`}>
      {isLoading || !track ? (
        <div className="py-8 flex justify-center"><Spinner /></div>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge color="gray">{track.category_display || "bez kategorii"}</Badge>
            {track.category_locked && <span className="text-[9px] font-mono text-[var(--color-text-muted)]">kategoria ręczna</span>}
            {clock && !clock.done && clock.side && clock.blocked_by?.length ? (
              <Badge color="gray">zablokowane</Badge>
            ) : clock && !clock.done && clock.side ? (
              <span className={clock.breached ? "text-red-700 font-bold" : clock.warn ? "text-amber-700 font-bold" : ""}>
                {clock.side === "ours" ? "MY" : clock.counterparty_label.toUpperCase()}:{" "}
                {clock.days_in}/{clock.limit_days ?? "—"} dni
                {clock.breached ? " — PO TERMINIE" : ""}
              </span>
            ) : null}
            {clock?.done && <Badge color="green">uzgodniona</Badge>}
          </div>
          {clock && !clock.done && (
            <p className="text-xs text-[var(--color-text-muted)]">
              {clock.blocked_by?.length
                ? `Etap jeszcze się nie otworzył — najpierw domknij: ${clock.blocked_by.join(", ")}.`
                : clock.phase_label}
            </p>
          )}
          {clock?.art124_needed && (
            <div className="text-xs border border-red-300 bg-red-50 text-red-800 px-2 py-1.5 rounded">
              Odmowa właściciela bez następnego kroku — dodaj bloczek „Zgłoszenie do ZE”
              (ponowne rokowania) albo „Art. 124”.
              {projectId && (
                <>
                  {" "}
                  <Link className="underline font-bold"
                    to={`/power-projects/${projectId}?tab=wnioski`}>
                    Przygotuj wniosek art. 124 w kreatorze →
                  </Link>
                </>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            {track.elements.map((el) => (
              <ElementRow key={el.id} el={el}
                onPatch={(body) => m.patch.mutate({ elementId: el.id, ...body })}
                onDelete={() => m.remove.mutate(el.id)} />
            ))}
            {!track.elements.length && (
              <p className="text-xs text-[var(--color-text-muted)]">Pusty tor — zegar liczy się z artefaktów (sprawy/WPW).</p>
            )}
          </div>

          {addOpen ? (
            <div className="border border-[var(--color-border)] rounded p-2 space-y-2">
              <div className="flex flex-wrap items-end gap-2 text-xs">
                <label className="flex flex-col gap-1">
                  <span className="section-label">Rodzaj</span>
                  <select className="input-base" value={kind} onChange={(e) => setKind(e.target.value)}>
                    {(track.element_kinds ?? []).map((k) => (
                      <option key={k.key} value={k.key}>{k.label}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="section-label">Własny opis (opcjonalnie)</span>
                  <input className="input-base w-52" value={label} onChange={(e) => setLabel(e.target.value)} />
                </label>
                {kind === "inny" && (
                  <>
                    <label className="flex flex-col gap-1">
                      <span className="section-label">My [dni]</span>
                      <input type="number" className="input-base w-16" value={ourDays} onChange={(e) => setOurDays(e.target.value)} />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="section-label">Oni [dni]</span>
                      <input type="number" className="input-base w-16" value={theirDays} onChange={(e) => setTheirDays(e.target.value)} />
                    </label>
                  </>
                )}
                <Button size="sm" loading={m.add.isPending} onClick={() => {
                  m.add.mutate({
                    kind, label,
                    manual_our_days: kind === "inny" && ourDays ? Number(ourDays) : undefined,
                    manual_their_days: kind === "inny" && theirDays ? Number(theirDays) : undefined,
                  }, { onSuccess: () => { setAddOpen(false); setLabel(""); } });
                }}>Dodaj</Button>
                <button className="text-xs text-[var(--color-text-muted)]" onClick={() => setAddOpen(false)}>anuluj</button>
              </div>
              <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
                {(track.element_kinds ?? []).find((k) => k.key === kind)?.help_text ?? ""}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button size="sm" variant="secondary" onClick={() => setAddOpen(true)}>+ Dodaj element toru</Button>
              <button className="text-[10px] text-[var(--color-text-muted)] underline"
                title="Dosiej zaplanowane bloczki z szablonu kategorii (zrobione/aktywne/ręczne bez zmian)."
                onClick={() => m.reset.mutate()}>
                resetuj zaplanowane z szablonu
              </button>
            </div>
          )}

          {(clock?.queued_kinds?.length ?? 0) > 0 && (
            <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
              W kolejce: {clock!.queued_kinds.map((k) => ELEMENT_KIND_LABELS[k] ?? k).join(" → ")}
              {" "}· prognoza działki: ~{clock!.remaining_forecast_days} dni
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
